import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostBinding, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonDimensions } from 'src/app/models/collection.interface';
import { CollectionsService } from 'src/app/services/collections.service';
import { ConfirmationModalComponent } from 'src/app/confirmation-modal/confirmation-modal.component';
import { Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CopyDocumentsDialogComponent } from '../copy-documents-dialog/copy-documents-dialog.component';
import { MoveDocumentsDialogComponent } from '../move-documents-dialog/move-documents-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
@Component({
  selector: 'app-selected-documents-menu',
  standalone: true,
  imports: [MatIconModule, MatButtonModule],
  templateUrl: './selected-documents-menu.component.html',
  styleUrl: './selected-documents-menu.component.scss',
  animations: [
    trigger('slideInOut', [
      state(
        'in',
        style({
          transform: 'translateY(0)' // Fully visible
        })
      ),
      state(
        'out',
        style({
          transform: 'translateY(100%)' // Hidden below the screen
        })
      ),
      transition('in => out', animate('300ms ease-in-out')), // Animation when closing
      transition('out => in', animate('300ms ease-in-out')) // Animation when opening
    ])
  ]
})
export class SelectedDocumentsMenuComponent {
  @Input() selectedDocuments: CommonDimensions[] = [];
  @Input() visible = false;
  @Output() refreshCollections = new EventEmitter<void>();

  constructor(
    private snackBar: MatSnackBar,
    private collectionsService: CollectionsService,
    private dialog: MatDialog
  ) {}

  @HostBinding('@slideInOut')
  get slideInOutState() {
    return this.visible ? 'in' : 'out';
  }

  showNotImplementedSnackbar(): void {
    this.snackBar.open('Not implemented yet', 'Close', {
      duration: 3000
    });
  }

  moveSelectedDocuments(): void {
    this.collectionsService.getCollections().subscribe(collections => {
      const dialogRef = this.dialog.open(MoveDocumentsDialogComponent, {
        width: '400px',
        data: { collections }
      });

      dialogRef.afterClosed().subscribe(targetCollectionId => {
        if (targetCollectionId) {
          const sourceCollectionId = this.selectedDocuments[0]['collection_id'].split('#')[1];
          const documentIds = this.selectedDocuments.map(doc => doc['file_id'].replace('file#', ''));
          this.collectionsService
            .moveDocumentsToCollection(sourceCollectionId, targetCollectionId, documentIds)
            .subscribe({
              next: () => {
                this.snackBar.open('Documents moved successfully', 'Close', {
                  duration: 5000
                });
                this.refreshCollections.emit();
              },
              error: (error: HttpErrorResponse) => {
                console.error('Error moving documents:', error);
                this.snackBar.open('Error moving documents', 'Close', {
                  duration: 3000
                });
              }
            });
        }
      });
    });
  }
  copySelectedDocuments(): void {
    this.collectionsService.getCollections().subscribe(collections => {
      const dialogRef = this.dialog.open(CopyDocumentsDialogComponent, {
        width: '400px',
        data: { collections }
      });

      dialogRef.afterClosed().subscribe(targetCollectionId => {
        if (targetCollectionId) {
          const sourceCollectionId = this.selectedDocuments[0]['collection_id'].split('#')[1];
          const documentIds = this.selectedDocuments.map(doc => doc['file_id'].replace('file#', ''));
          this.collectionsService
            .copyDocumentsToCollection(sourceCollectionId, targetCollectionId, documentIds)
            .subscribe({
              next: () => {
                this.snackBar.open('Documents copied successfully', 'Close', {
                  duration: 5000
                });
                this.refreshCollections.emit();
              },
              error: (error: HttpErrorResponse) => {
                console.error('Error copying documents:', error);
                this.snackBar.open('Error copying documents', 'Close', {
                  duration: 3000
                });
              }
            });
        }
      });
    });
  }

  confirmRemoveSelectedDocuments(): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: {
        message: `You are about to permanently delete selected documents and related vector store records.
                          This operation cannot be undone.
                          Are you sure you want to proceed?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeSelectedDocuments();
      }
    });
  }

  removeSelectedDocuments(): void {
    if (this.selectedDocuments.length === 0) {
      this.snackBar.open('No documents selected', 'Close', {
        duration: 3000
      });
      return;
    }
    const documents = this.selectedDocuments;
    const collectionId = documents[0]['collection_id'].split('#')[1];
    const documentIds = documents.map(doc => doc['file_id'].replace('file#', ''));

    this.collectionsService.deleteFilesFromCollection(collectionId, documentIds).subscribe({
      next: () => {
        this.snackBar.open('Documents removed successfully', 'Close', {
          duration: 5000
        });
        this.selectedDocuments = []; // Clear the selected documents after removal
        this.refreshCollections.emit(); // refresh document list
      },
      error: error => {
        console.error('Error removing documents:', error);
        this.snackBar.open('Error removing documents', 'Close', {
          duration: 3000
        });
      }
    });

    // this.showNotImplementedSnackbar();
  }
  downloadSelectedDocuments(): void {
    if (this.selectedDocuments.length === 0) {
      this.snackBar.open('No documents selected', 'Close', {
        duration: 3000
      });
      return;
    }
    if (this.selectedDocuments.length > 1) {
      this.snackBar.open('Can only open 1 document at a time', 'Close', {
        duration: 3000
      });
      return;
    }

    const document = this.selectedDocuments[0];
    const collectionId = document['collection_id'].split('#')[1];
    this.collectionsService.downloadFileFromCollection(collectionId, document['original_document_uri']).subscribe({
      next: ({ file_name, url }) => {
        this.snackBar.open(`Opening ${file_name}`, 'Close', {
          duration: 10000
        });
        setTimeout(() => window.open(url, '_blank'), 2000);
      },
      error: error => {
        console.error('Error downloading document:', error);
        this.snackBar.open('Error downloading document', 'Close', {
          duration: 3000
        });
      }
    });
  }
}
